import React, {useEffect} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Button, Dialog, Grid, Typography} from "@material-ui/core";
import EnhancedMappingEditor from "../../../../export/components/EnhancedMappingEditor";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {useGlobalStyles} from "../../../../../utils/styles";
import {Field, FormSpy} from "react-final-form";
import EnhancedAutocomplete from "../../../../../components/EnhancedAutocomplete";
import {utilTestColumnMapping} from "../../../../../utils/utils";
import {formatProfileAttributeRulesToRules, formatProfileCategoryMappingsToRules, formatProfileListMappingsToRules, formatProfileToColumns} from "../Review";
import {getErrorMessage} from "../../../../../../inventory/mapping_checker";
import {makeStyles} from "@material-ui/core/styles";
import {parseCondition} from "../../../../../../inventory/mapping_templates";

export default function ColumnRuleEditor(props) {
    const {open, setOpen} = props;
    const {attributes, parsedFields, sampleData} = props;
    const {profile, setProfile} = props;
    const {isCalculatedColumn} = props;

    const styles = useStyles();

    const [currentProfile, setCurrentProfile] = React.useState(profile);
    const [currentGroupedRules, setCurrentGroupedRules] = React.useState(() =>
        (props.groupedRule ?? []).map(rule => ({
            ...rule,
            id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        }))
    );
    const [rulesOpen, setRulesOpen] = React.useState((props.groupedRule ?? []).map(() => false));
    const [testResults, setTestResults] = React.useState([]);
    const [testError, setTestError] = React.useState();

    // This is a list of refs for each rule in the editor, this is required due to how the EnhancedMappingEditor component works
    // as its inner component is a Form, normally we call an onSubmit function to save the form values, but in this case we need to
    // monior form values in real time as its not in its own contained dialog.
    const [ruleRefs, setRuleRefs] = React.useState(() =>
        (props.groupedRule ?? []).map(() => React.createRef())
    );

    const globalClasses = useGlobalStyles();

    const mappingOptions = attributes.map(attr => ({value: attr.Name, label: attr.FriendlyName}));
    const parsedFieldsOptions = parsedFields.map(field => ({value: field.field, label: field.field}));

    const save = () => {
        const newRules = ruleRefs.map(ref => {
            return {
                TargetField: ref.current.TargetField,
                SourceField: ref.current.SourceField,
                Condition: ref.current.Condition
            }
        });

        const existingRules = currentProfile.AttributeMappingRules.filter(rule => rule.TargetField !== props.column);

        setProfile({
            ...currentProfile,
            AttributeMappingRules: [
                ...existingRules,
                ...newRules
            ]
        });
        setOpen(false);
    }

    const addRule = () => {
        if(currentGroupedRules.length === 0 && !isCalculatedColumn) {
            const copiedProfile = {...currentProfile};
            delete copiedProfile.AttributeMappings[props.column];
            copiedProfile.AttributeMappings[props.column] = props.column;
            setCurrentProfile(copiedProfile);
        }

        const newRuleId = `rule-${Date.now()}`;
        const newRule = {
            id: newRuleId,
            SourceField: "",
            TargetField: props.column
        };

        setCurrentGroupedRules(prevRules => [...prevRules, newRule]);
        setRuleRefs(prevRefs => [...prevRefs, React.createRef()]);
        setRulesOpen(prevOpen => [...prevOpen, true]);
    };

    const runTests = async () => {
        const newRules = ruleRefs.map(ref => {
            return {
                TargetField: ref.current.TargetField,
                SourceField: ref.current.SourceField,
                Condition: ref.current.Condition
            }
        });

        const existingRules = currentProfile.AttributeMappingRules.filter(rule => rule.TargetField !== props.column);
        const combinedRules = [
            ...existingRules,
            ...newRules
        ];

        const hasColumnMapping = Object.keys(currentProfile.AttributeMappings).find(key => key === props.column) !== undefined;
        const tempProfile = {
            ...currentProfile,
            AttributeMappingRules: combinedRules
        }

        if(!hasColumnMapping) {
            const copiedProfile = {...tempProfile};
            delete copiedProfile.AttributeMappings[props.column];
            copiedProfile.AttributeMappings[props.column] = props.column;
            tempProfile.AttributeMappings = copiedProfile.AttributeMappings;
        }

        const formattedColumns = formatProfileToColumns(tempProfile, parsedFields);
        const formattedListMappings = formatProfileListMappingsToRules(tempProfile, parsedFields);
        const formattedCategoryMappings = formatProfileCategoryMappingsToRules(tempProfile, parsedFields);
        const formattedAttributeRules = formatProfileAttributeRulesToRules(tempProfile, parsedFields);

        const newFormattedRules= [
            ...formattedListMappings,
            ...formattedCategoryMappings,
            ...formattedAttributeRules
        ];

        const response = await utilTestColumnMapping(sampleData, formattedColumns, newFormattedRules);
        if(response.success) {
            setTestResults(response.data);
            setTestError();
        }
        else {
            setTestResults([]);
            setTestError(response.error);
            console.error(response.error);
        }
    }

    const removeRule = (ruleId) => {
        const indexToRemove = currentGroupedRules.findIndex(rule => rule.id === ruleId);
        if (indexToRemove === -1) return;

        setCurrentGroupedRules(prevRules =>
            prevRules.filter(rule => rule.id !== ruleId)
        );

        setRuleRefs(prevRefs => {
            const newRefs = [...prevRefs];
            newRefs.splice(indexToRemove, 1);
            return newRefs;
        });

        setRulesOpen(prevOpen => {
            const newOpen = [...prevOpen];
            newOpen.splice(indexToRemove, 1);
            return newOpen;
        });
    };

    useEffect(() => {
        if (props.groupedRule) {
            setCurrentGroupedRules(props.groupedRule.map(rule => ({
                ...rule,
                id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            })));
            setRulesOpen(props.groupedRule.map(() => false));
            setRuleRefs(props.groupedRule.map(() => React.createRef()));
            setTestError();
            setTestResults([]);
        }
    }, [props.groupedRule]);

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth={"lg"} fullWidth={true}>
            <Grid container style={{gap: "20px", padding: "20px"}}>
                <Grid container style={{flex: 1, flexDirection: "column"}}>
                    <Grid container style={{flex: 0}}>
                        <Typography variant={"h6"}>
                            Rules for: {props.column}
                        </Typography>
                    </Grid>

                    <Grid container style={{flex: 0, margin: "10px 0"}}>
                        <Grid container style={{flex: 1, gap: "10px"}}>
                            <Button variant={"outlined"}
                                    onClick={() => setRulesOpen(rulesOpen.map(() => true))}
                            >
                                Expand All Rules
                            </Button>

                            <Button variant={"outlined"}
                                    onClick={() => setRulesOpen(rulesOpen.map(() => false))}
                            >
                                Collapse All Rules
                            </Button>
                        </Grid>

                        <Grid container style={{flex: 0.3, justifyContent: "flex-end"}}>
                            <Button variant={"outlined"}
                                    onClick={() => {
                                        setCurrentGroupedRules([]);
                                        setRuleRefs([]);
                                        setRulesOpen([]);
                                    }}
                            >
                                Remove All Rules
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container style={{flex: 1, paddingTop: "5px", borderTop: "lightgray solid 1px", gap: "10px", flexDirection: "column"}}>
                        {currentGroupedRules.map((rule, index) => {
                            return (
                                <Accordion
                                    key={rule.id}
                                    onChange={(e, expanded) => {
                                        setRulesOpen(rulesOpen.map((r, i) => {
                                            if (i === index) {
                                                return expanded;
                                            }

                                            return r;
                                        }));
                                    }}
                                    expanded={rulesOpen[index]}
                                    elevation={0}
                                    classes={{
                                        root: globalClasses.collapsableTab
                                    }}
                                    style={{
                                        width: "100%",
                                        flex: 0
                                    }}
                                >
                                    <AccordionSummary
                                        classes={{
                                            expandIcon: styles.expandIcon,
                                            expanded: styles.expanded
                                        }}
                                        expandIcon={<ExpandMoreIcon style={{color: 'black'}} />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                    >
                                        <Grid container style={{alignItems: "center", gap: "10px"}}>
                                            <Typography variant={"h6"}>
                                                Rule {index + 1}:
                                            </Typography>

                                            <Typography style={{fontSize: "16px"}}>
                                                {ruleRefs[index]?.current?.SourceField ?? (rule.SourceField ?? "")}
                                            </Typography>

                                            <Typography style={{fontSize: "16px"}}>
                                                {(ruleRefs[index]?.current?.Condition || rule.Condition)
                                                    ? "when"
                                                    : ""
                                                }
                                            </Typography>

                                            <Typography style={{fontSize: "16px"}}>
                                                {parseCondition(ruleRefs[index]?.current?.Condition ?? (rule.Condition ?? ""), parsedFields)}
                                            </Typography>
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails style={{flexDirection: "column"}}>
                                        <EnhancedMappingEditor
                                            key={`editor-${rule.id}`}

                                            options={[...mappingOptions, ...parsedFieldsOptions]}
                                            
                                            condition={rule.Condition}

                                            source={rule.SourceField}
                                            target={rule.TargetField}

                                            styles={{flex: 1}}

                                            title={""}
                                            notDialog={true}

                                            formValuesRef={ruleRefs[index]}

                                            beforeForm={() => {
                                                return (
                                                    <FormSpy subscription={{name: true, values: true, pristine: true}}>
                                                        {({name, values, pristine}) => {
                                                            return (
                                                                <>
                                                                    <Grid item container style={{gap: "12px"}}>
                                                                        <Field name={`TargetField`}
                                                                               component={(props) => {
                                                                                   return (
                                                                                       <EnhancedAutocomplete
                                                                                           containerStyle={{flex: 1}}
                                                                                           disabled={true}
                                                                                           label={props.targetFieldTitle || "Output Column"}
                                                                                           notMultiple={true}
                                                                                           promptDisabled={true}
                                                                                           value={props.input.value || null}
                                                                                           options={[].map(option => option.value)}
                                                                                           onChange={(e, value) => {
                                                                                               props.input.onChange(value);
                                                                                           }}
                                                                                       />
                                                                                   );
                                                                               }}/>

                                                                        <Field name={`SourceField`}
                                                                               validate={v => getErrorMessage(v === null ? undefined : v, parsedFields.map(f => f.field))}
                                                                               component={(props) => {
                                                                                   let error = props.meta ? props.meta.error : undefined;
                                                                                   let helperText = props.meta
                                                                                       ? props.meta.error
                                                                                           ? props.meta.error : 'Please enclose constant values in quotes.'
                                                                                       : 'Please enclose constant values in quotes.';

                                                                                   return (
                                                                                       <EnhancedAutocomplete
                                                                                           error={error}
                                                                                           helperText={helperText}
                                                                                           containerStyle={{flex: 1}}
                                                                                           label={props.sourceFieldTitle || "Input Field/Expression"}
                                                                                           notMultiple={true}
                                                                                           promptDisabled={true}
                                                                                           value={props.input.value || null}
                                                                                           options={[].map(option => option.value)}
                                                                                           onChange={(e, value) => {
                                                                                               props.input.onChange(value);
                                                                                           }}
                                                                                       />
                                                                                   );
                                                                               }}/>
                                                                    </Grid>
                                                                </>
                                                            )
                                                        }}
                                                    </FormSpy>
                                                )
                                            }}
                                        />

                                        <Button
                                            className={globalClasses.deleteButton}
                                            style={{
                                                width: "120px",
                                                margin: "10px 0"
                                            }}
                                            variant={"contained"}
                                            onClick={() => removeRule(rule.id)}
                                        >
                                            Remove
                                        </Button>
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}

                        <Button variant={"outlined"}
                                style={{height: "40px", width: "125px"}}
                                onClick={() => addRule()}
                        >
                            + Add Rule
                        </Button>
                    </Grid>

                    <Grid container style={{justifyContent: "flex-end", gap: "10px", marginTop: "10px", paddingTop: "10px", borderTop: "lightgray solid 1px"}}>
                        <Button variant={"outlined"} onClick={() => runTests()}>
                            Test
                        </Button>

                        <Button variant={"outlined"}
                                onClick={() => setOpen(false)}
                        >
                            Cancel
                        </Button>

                        <Button variant={"outlined"}
                                onClick={() => save()}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>

                <Grid container style={{flex: 0.3, flexDirection: "column", paddingLeft: "10px", borderLeft: "lightgray solid 1px", overflow: "auto", gap: "10px"}}>
                    <Grid container>
                        <Typography variant={"h6"} style={{width: "100%"}}>
                            Input Variables
                        </Typography>

                        <Grid container style={{maxHeight: "200px", overflowY: "auto"}}>
                            {parsedFields.map((field, index) => {
                                return (
                                    <Typography variant={"body1"} style={{width: "100%"}} key={index}>
                                        {field.field}
                                    </Typography>
                                );
                            })}
                        </Grid>
                    </Grid>

                    <Grid container style={{flexDirection: "column"}}>
                        <Typography variant={"h6"} style={{flex: 1}}>
                            Expression Operators
                        </Typography>

                        <Grid>
                            {["GetStartDate", "GetEndDate", "GetDateRange", "AddDays", "Replace", "Get", "Parse", "Upper", "Lower"].map((op, index) => {
                                return (
                                    <Typography variant={"body1"} key={index}>
                                        {op}
                                    </Typography>
                                );
                            })}
                        </Grid>
                    </Grid>

                    <Grid container style={{flexDirection: "column"}}>
                        <Typography variant={"h6"} style={{flex: 1}}>
                            Test Results
                        </Typography>

                        {testError ? (
                            <Typography variant={"body1"} color={"error"}>
                                {testError}
                            </Typography>
                        ) : (
                            <>
                                {testResults.length > 0
                                    ? (
                                        <Grid container>
                                            <Grid container style={{borderBottom: "1px solid lightgray"}}>
                                                <Grid item xs={6}>
                                                    <Typography variant={"subtitle1"} style={{fontWeight: "bold"}}>
                                                        SKU
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={6}>
                                                    <Typography variant={"subtitle1"} style={{fontWeight: "bold"}}>
                                                        Sample Output
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {sampleData.map((row, index) => {
                                                return (
                                                    <Grid container key={index}>
                                                        <Grid item xs={6}>
                                                            <Typography variant={"body1"} style={{textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
                                                                {row.sku}
                                                            </Typography>
                                                        </Grid>

                                                        <Grid item xs={6}>
                                                            <Typography variant={"body1"} style={{textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
                                                                {testResults[index][props.column]}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    ) : (
                                        <Typography variant={"body1"}>
                                            No test results.
                                        </Typography>
                                    )
                                }
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
}

const useStyles = makeStyles((theme) => ({
    expandIcon: {
        transform: "rotate(90deg)",
        "&$expanded": {
            transform: "rotate(0deg)"
        }
    },
    expanded: {},
}));
